import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const ReturnsPage: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 animate-fade-in">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <p className="text-xs tracking-widest uppercase text-nude mb-3">
          Customer Care
        </p>
        <h1 className="font-serif text-5xl text-primary mb-12">
          Returns & Exchanges
        </h1>

        <div className="space-y-10 text-sm text-secondary leading-relaxed tracking-wide">
          <div className="bg-light p-8">
            <p className="font-serif text-2xl text-primary mb-3">
              30-Day Return Policy
            </p>
            <p>
              We accept returns within 30 days of delivery for unworn,
              unwashed items in their original condition with all tags
              attached.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-primary mb-4">
              Eligibility
            </h2>
            <p className="mb-4">Items must meet the following criteria:</p>
            <ul className="space-y-2 text-xs">
              {[
                'Unworn and unwashed',
                'Original tags attached',
                'Original packaging included',
                'Returned within 30 days of delivery',
                'Not marked as final sale',
              ].map((item) => (
                <li key={item} className="flex items-start space-x-2">
                  <span className="text-nude mt-0.5">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-primary mb-4">
              How to Return
            </h2>
            <div className="space-y-4">
              {[
                {
                  step: '01',
                  title: 'Initiate Return',
                  desc: 'Log in to your account and navigate to your order history. Select the item you wish to return.',
                },
                {
                  step: '02',
                  title: 'Pack Your Item',
                  desc: 'Carefully pack the item in its original packaging with all tags attached.',
                },
                {
                  step: '03',
                  title: 'Ship It Back',
                  desc: 'Use the prepaid return label provided or ship to our returns address at your own cost.',
                },
                {
                  step: '04',
                  title: 'Receive Refund',
                  desc: 'Once we receive and inspect your return, we will process your refund within 5-7 business days.',
                },
              ].map((item) => (
                <div key={item.step} className="flex space-x-6">
                  <span className="font-serif text-3xl text-nude flex-shrink-0">
                    {item.step}
                  </span>
                  <div>
                    <p className="font-medium text-primary mb-1">{item.title}</p>
                    <p className="text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-primary mb-4">
              Exchanges
            </h2>
            <p>
              We offer exchanges for different sizes or colours subject to
              availability. To request an exchange, please contact our
              customer service team with your order number and the item
              details you would like to exchange for.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-primary mb-4">
              Refunds
            </h2>
            <p className="mb-4">
              Refunds will be processed to your original payment method
              within 5-7 business days of receiving your return. Shipping
              costs are non-refundable unless the return is due to our error.
            </p>
            <p>
              For Paystack payments, refunds will be processed to your
              original payment method. For PayPal payments, refunds will
              be returned to your PayPal account.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-primary mb-4">
              Non-Returnable Items
            </h2>
            <p>
              The following items cannot be returned: final sale items,
              intimate apparel, customised or personalised items, and items
              marked as non-returnable at the time of purchase.
            </p>
          </div>

          <div className="border-t border-light pt-8">
            <p className="text-xs text-nude mb-4">
              Need help with a return?
            </p>
            <Link to="/contact" className="btn-primary inline-block">
              Contact Us
            </Link>
          </div>

          <p className="text-xs text-nude">Last updated: January 2026</p>
        </div>
      </motion.div>
    </div>
  );
};

export default ReturnsPage;